import React, { useCallback, useRef, useState } from 'react'
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd'
import Icon from '../Icon2'
import TextInput from '../TextInput/TextInput'
import Dropdown from '../DropDown/Dropdown'
import ValidationMessage from '../ValidationMessage/ValidationMessage'
import './SortableList.css'

export type SortableListMode = 'value' | 'keyValue'

/** Quick-actions menu open direction. `auto` opens right, flipping left when the
 *  viewport lacks room on the right (e.g. the list sits in a right-hand panel). */
export type SortableListActionsPosition = 'auto' | 'left' | 'right'

export interface SortableListItem {
  /** Stable, unique id — used as the react-beautiful-dnd draggableId. */
  id: string
  /** Shown as the key input in `keyValue` mode. */
  key?: string
  /** Shown as the value input (both modes). */
  value: string
  /** When true (and `defaultBadgeLabel` is set), renders the Default badge on the row. */
  isDefault?: boolean
  /** Validation message for the key input; also puts the input in an error state. */
  keyError?: string
  /** Validation message for the value input; also puts the input in an error state. */
  valueError?: string
  /** Locks this row (inputs disabled, drag disabled). */
  disabled?: boolean
}

export interface SortableListAction {
  label: string
  /** venus Icon2 name (optional leading icon in the menu). */
  icon?: string
  onClick: (item: SortableListItem, index: number) => void
  disabled?: boolean
  /** Renders the action in a destructive (delete) style. */
  destructive?: boolean
  testId?: string
}

export interface SortableListProps {
  /** Ordered rows. Reordering is derived from array position. */
  items: SortableListItem[]
  /** `value` = single input per row; `keyValue` = key + value split. Default `value`. */
  mode?: SortableListMode
  /** Called after a drop with the already-reordered array (arrayMove applied internally). */
  onReorder: (items: SortableListItem[], meta: { from: number; to: number }) => void
  /** Called when a row's value input changes. */
  onValueChange: (index: number, value: string) => void
  /** Called when a row's key input changes (required in `keyValue` mode). */
  onKeyChange?: (index: number, key: string) => void
  /** Label for the Default badge (text owned by the consumer). Omit to disable the badge entirely. */
  defaultBadgeLabel?: string
  /** Quick-actions (kebab) applied to every row. Consumer-supplied; omit (and omit
   *  `getActions`) to hide the kebab column entirely. */
  actions?: SortableListAction[]
  /** Per-row quick-actions (kebab) — use when a row's menu depends on its state
   *  (e.g. "Mark as Default" ↔ "Remove as Default"). Takes precedence over `actions`. */
  getActions?: (item: SortableListItem, index: number) => SortableListAction[]
  /** Kebab menu open direction. Default `auto` (open right, flip left when no room). */
  actionsPosition?: SortableListActionsPosition
  keyPlaceholder?: string
  valuePlaceholder?: string
  /** Disables the whole list (drag + inputs). Default false. */
  isDisabled?: boolean
  testId?: string
}

/** Move an item within a copy of the array (does not mutate the input). */
function arrayMove<T>(arr: T[], from: number, to: number): T[] {
  const next = arr.slice()
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

/** Approx. menu width used to decide whether the kebab has room to open right. */
const KEBAB_MENU_WIDTH = 200

interface KebabMenuProps {
  actions: SortableListAction[]
  item: SortableListItem
  index: number
  position: SortableListActionsPosition
  testId?: string
}

/**
 * The per-row quick-actions kebab. Wraps the venus Dropdown to add auto-flip:
 * venus positions the menu with a static class, so for `position="auto"` we measure
 * the trigger against the viewport on open and pick right (default) or left (no room).
 */
const KebabMenu: React.FC<KebabMenuProps> = ({ actions, item, index, position, testId }) => {
  const triggerRef = useRef<HTMLSpanElement>(null)
  const [resolved, setResolved] = useState<'left' | 'right'>(position === 'auto' ? 'right' : position)

  // Runs on mousedown (before the Dropdown's click toggle) so the direction is set
  // before the menu paints.
  const resolvePosition = useCallback(() => {
    if (position !== 'auto') {
      setResolved(position)
      return
    }
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect || typeof window === 'undefined') return
    const roomOnRight = window.innerWidth - rect.right
    setResolved(roomOnRight >= KEBAB_MENU_WIDTH ? 'right' : 'left')
  }, [position])

  const list = actions.map((a) => ({
    label: (
      <div
        className={`SortableList__action${a.destructive ? ' SortableList__action--destructive' : ''}`}
      >
        {a.icon ? (
          <Icon icon={a.icon} version="v2" size="tiny" className="SortableList__action-icon" />
        ) : null}
        <span>{a.label}</span>
      </div>
    ),
    textLabel: a.label,
    disable: a.disabled,
    testId: a.testId,
    action: () => a.onClick(item, index)
  }))

  return (
    <Dropdown
      className="SortableList__kebab"
      list={list}
      type="click"
      dropDownPosition={resolved}
      closeAfterSelect
      version="v2"
    >
      <span
        ref={triggerRef}
        className="SortableList__kebab-trigger"
        onMouseDown={resolvePosition}
        data-test-id={testId}
      >
        <Icon icon="DotsThreeLargeVertical" version="v2" size="small" />
      </span>
    </Dropdown>
  )
}

/**
 * A prop-driven, drag-to-reorder list of editable choice rows, built on
 * react-beautiful-dnd. Venus owns the mechanics + row chrome (drag handle,
 * inputs, Default badge, quick-actions kebab, key-value split, validation);
 * the consumer supplies all data, labels, and callbacks. Reorder happens via
 * the handle only, so the inputs stay editable.
 */
const SortableList: React.FC<SortableListProps> = ({
  items,
  mode = 'value',
  onReorder,
  onValueChange,
  onKeyChange,
  defaultBadgeLabel,
  actions,
  getActions,
  actionsPosition = 'auto',
  keyPlaceholder = 'Enter key',
  valuePlaceholder = 'Enter value',
  isDisabled = false,
  testId
}) => {
  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result
    if (!destination || destination.index === source.index) return
    onReorder(arrayMove(items, source.index, destination.index), {
      from: source.index,
      to: destination.index
    })
  }

  const rowTestId = (suffix: string, index: number) =>
    testId ? `${testId}-${suffix}-${index}` : undefined

  const renderDefaultBadge = (item: SortableListItem, index: number) =>
    item.isDefault && defaultBadgeLabel ? (
      <span className="SortableList__badge" data-test-id={rowTestId('badge', index)}>
        {defaultBadgeLabel}
      </span>
    ) : undefined

  const renderValueInput = (item: SortableListItem, index: number) => (
    <TextInput
      version="v2"
      width="full"
      value={item.value}
      placeholder={valuePlaceholder}
      error={!!item.valueError}
      disabled={isDisabled || item.disabled}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onValueChange(index, e.target.value)}
      suffixVisible={!!(item.isDefault && defaultBadgeLabel)}
      suffix={renderDefaultBadge(item, index)}
      testId={rowTestId('value', index)}
    />
  )

  const renderKeyInput = (item: SortableListItem, index: number) => (
    <TextInput
      version="v2"
      width="full"
      value={item.key || ''}
      placeholder={keyPlaceholder}
      error={!!item.keyError}
      disabled={isDisabled || item.disabled}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        onKeyChange && onKeyChange(index, e.target.value)
      }
      testId={rowTestId('key', index)}
    />
  )

  const renderKebab = (item: SortableListItem, index: number) => {
    // getActions (per-row) wins; else the static actions list; else no kebab.
    const rowActions = getActions ? getActions(item, index) || [] : actions || []
    if (!rowActions.length) return null
    return (
      <KebabMenu
        actions={rowActions}
        item={item}
        index={index}
        position={actionsPosition}
        testId={rowTestId('actions', index)}
      />
    )
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="venus-sortable-list" isDropDisabled={isDisabled}>
        {(dropProvided) => (
          <div
            ref={dropProvided.innerRef}
            {...dropProvided.droppableProps}
            className="SortableList"
            data-test-id={testId}
          >
            {items.map((item, index) => {
              const hasError = !!(item.valueError || item.keyError)
              const kebab = renderKebab(item, index)
              return (
                <Draggable
                  key={item.id}
                  draggableId={item.id}
                  index={index}
                  isDragDisabled={isDisabled || !!item.disabled}
                >
                  {(dragProvided, snapshot) => (
                    <div
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      className={`SortableList__row${snapshot.isDragging ? ' SortableList__row--dragging' : ''}`}
                      data-test-id={rowTestId('row', index)}
                    >
                      <div className="SortableList__controls">
                        <span
                          className="SortableList__handle"
                          {...dragProvided.dragHandleProps}
                          aria-label="Drag to reorder"
                          data-test-id={rowTestId('handle', index)}
                        >
                          <Icon icon="DotsSixVertical" version="v2" size="medium" />
                        </span>

                        {mode === 'keyValue' ? (
                          <div className="SortableList__kv">
                            <div className="SortableList__kv-field">
                              {renderValueInput(item, index)}
                            </div>
                            <span className="SortableList__kv-separator" aria-hidden="true" />
                            <div className="SortableList__kv-field">{renderKeyInput(item, index)}</div>
                          </div>
                        ) : (
                          <div className="SortableList__field">{renderValueInput(item, index)}</div>
                        )}

                        {kebab}
                      </div>

                      {/* Error row reuses the input row's exact layout (handle-width spacer +
                          same key/value columns + a kebab-width spacer only when a kebab
                          exists) so each message lines up precisely under its own field. */}
                      {hasError ? (
                        <div className="SortableList__controls SortableList__controls--errors">
                          <span className="SortableList__handle-spacer" aria-hidden="true" />
                          {mode === 'keyValue' ? (
                            // Value column first, key column second — must match the input row order.
                            <div className="SortableList__kv">
                              <div className="SortableList__kv-field">
                                {item.valueError ? (
                                  <ValidationMessage version="v2" testId={rowTestId('value-error', index)}>
                                    {item.valueError}
                                  </ValidationMessage>
                                ) : null}
                              </div>
                              <span className="SortableList__kv-error-sep" aria-hidden="true" />
                              <div className="SortableList__kv-field">
                                {item.keyError ? (
                                  <ValidationMessage version="v2" testId={rowTestId('key-error', index)}>
                                    {item.keyError}
                                  </ValidationMessage>
                                ) : null}
                              </div>
                            </div>
                          ) : (
                            <div className="SortableList__field">
                              <ValidationMessage version="v2" testId={rowTestId('error', index)}>
                                {item.valueError || item.keyError}
                              </ValidationMessage>
                            </div>
                          )}
                          {kebab ? <span className="SortableList__kebab-spacer" aria-hidden="true" /> : null}
                        </div>
                      ) : null}
                    </div>
                  )}
                </Draggable>
              )
            })}
            {dropProvided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  )
}

export default SortableList
